import { StepProps } from '@/types/broker-registration.types';
import { FormField } from '../FormField';
import { CheckboxGroup } from '../CheckboxGroup';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  getTransactionTypeOptions,
} from '@/utils/broker-registration-options';

// All NDA enforcement options - broker must agree to ALL
const NDA_REQUIRED_VALUES = [
  'prefinancing_share',
  'standardized_documents',
  'positive_cashflow_only',
  'acquizgroup_evaluation',
];

export function StepThree({ formData, updateFormData, errors, clearError }: StepProps) {
  const { t, language } = useLanguage();

  const ndaEnforcementOptions = [
    {
      value: 'prefinancing_share',
      label: t('broker_registration.step_three.nda_prefinancing_share'),
    },
    {
      value: 'standardized_documents',
      label: t('broker_registration.step_three.nda_standardized_documents'),
    },
    {
      value: 'positive_cashflow_only',
      label: t('broker_registration.step_three.nda_positive_cashflow'),
    },
    {
      value: 'acquizgroup_evaluation',
      label: t('broker_registration.step_three.nda_evaluation'),
    },
  ];

  // Check if all NDA options are selected
  const allNdaSelected = NDA_REQUIRED_VALUES.every(v => formData.ndaEnforcement.includes(v));
  const missingCount = NDA_REQUIRED_VALUES.length - formData.ndaEnforcement.filter(v => NDA_REQUIRED_VALUES.includes(v)).length;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold border-b border-slate-200 pb-3 text-[#1D314D]">
        {t('broker_registration.step_three.title')}
      </h2>
      <p className="text-sm text-slate-600 mb-6">
        {t('broker_registration.step_three.subtitle')}
      </p>

      <FormField
        label={t('broker_registration.step_three.transaction_types')}
        helperText={t('broker_registration.step_three.transaction_types_hint')}
        error={errors.transactionTypes}
        required
      >
        <CheckboxGroup
          values={formData.transactionTypes}
          onChange={(values) => {
            updateFormData({ transactionTypes: values });
            clearError('transactionTypes');
          }}
          options={getTransactionTypeOptions(t)}
          error={!!errors.transactionTypes}
        />
      </FormField>

      <FormField
        label={t('broker_registration.step_three.nda_enforcement')}
        helperText={t('broker_registration.step_three.nda_enforcement_hint')}
        error={errors.ndaEnforcement}
        required
      >
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4">
            <p className="text-amber-800 text-sm font-medium">
              {language === 'fr' 
                ? '⚠️ Vous devez accepter TOUS les énoncés ci-dessous pour continuer.'
                : '⚠️ You must agree to ALL statements below to continue.'}
            </p>
            {!allNdaSelected && formData.ndaEnforcement.length > 0 && (
              <p className="text-amber-700 text-xs mt-1">
                {language === 'fr' 
                  ? `${missingCount} énoncé(s) restant(s) à accepter.`
                  : `${missingCount} statement(s) remaining.`}
              </p>
            )}
          </div>
          <CheckboxGroup
            values={formData.ndaEnforcement}
            onChange={(values) => {
              updateFormData({ ndaEnforcement: values });
              clearError('ndaEnforcement');
            }}
            options={ndaEnforcementOptions}
            error={!!errors.ndaEnforcement}
            columns={1}
          />
          {allNdaSelected && (
            <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
              <p className="text-green-700 text-sm font-medium flex items-center">
                <span className="mr-2">✓</span>
                {language === 'fr' 
                  ? 'Tous les énoncés ont été acceptés.'
                  : 'All statements have been accepted.'}
              </p>
            </div>
          )}
        </div>
      </FormField>
    </div>
  );
}

export { NDA_REQUIRED_VALUES };
